import {
  VaccinationsTemplate,
  type VaccinationsTemplateRow,
} from '../../templates';
import { DocumentSource, DocumentType } from '../types';
import type { DocumentEntry, DocumentKey } from '../types';
import { isPdfSignedUrlCandidate } from './pdfOverride';
import type { RawPractitioner, RawSourceMetadata } from './_shared';

type VaccinationsDataKey = Extract<
  DocumentKey,
  { source: DocumentSource.MEDICAL_DATA; type: DocumentType.VACCINATIONS }
>;

interface RawVaccinationSource {
  id?: string;
  signedUrl?: string;
  contentType?: string;
  createdAt?: string | Date;
  dataType?: 'problems_json' | 'allergies_json';
  curatedProblem?: {
    id: string;
    name?: string;
    onsetDate?: string;
    clinicalStatus?: string;
    conditionNote?: string;
    recordedDate?: string;
    dateUploaded?: string;
    resolutionDate?: string;
    sourceMetadata?: RawSourceMetadata;
    patientId?: string;
    problemCodes?: string[];
    categoryCodes?: string[];
    categoryCodeName?: string;
  };
  curatedAllergy?: {
    id: string;
    name?: string;
    recordedDate?: string;
    onsetStartDate?: string;
    onsetEndDate?: string;
    reactionManifestation?: string;
    dateUploaded?: string;
    practitioner?: RawPractitioner;
    sourceMetadata?: RawSourceMetadata;
    patientId?: string;
    allergyCodes?: string[];
    reactionManifestationCodes?: string[];
  };
}

interface RawVaccination {
  id: string;
  name: string;
  route?: string;
  status?: string;
  dosageUnit?: string;
  dosageValue?: string;
  occurrenceTime?: string;
  site?: string;
  dateUploaded?: string;
  sources?: RawVaccinationSource[];
  sourceMetadata?: RawSourceMetadata;
  patientId?: string;
  immunizationCodes?: string[];
  routeCodes?: string[];
  siteCodes?: string[];
}

export interface VaccinationsDataQueryData {
  vaccinations: RawVaccination[];
}

const formatDose = (v: RawVaccination): string | undefined => {
  if (!v.dosageValue && !v.dosageUnit) return undefined;
  return [v.dosageValue, v.dosageUnit].filter(Boolean).join(' ');
};

const mapRow = (v: RawVaccination): VaccinationsTemplateRow => ({
  id: v.id,
  vaccine: v.name,
  route: v.route,
  status: v.status,
  dose: formatDose(v),
  occurrenceTime: v.occurrenceTime,
  site: v.site,
});

export const adaptVaccinationsData = (
  data: VaccinationsDataQueryData,
  key: VaccinationsDataKey,
): DocumentEntry | undefined => {
  const vaccinations = data?.vaccinations ?? [];
  if (vaccinations.length === 0) return undefined;

  const pdfSource = vaccinations
    .flatMap((v) => v.sources ?? [])
    .find((s) => isPdfSignedUrlCandidate(s));

  if (pdfSource?.signedUrl) {
    return {
      kind: 'pdf',
      signedUrl: pdfSource.signedUrl,
      title: key.name,
      uploadedAt: pdfSource.createdAt,
    };
  }

  const rows = vaccinations.map(mapRow);

  return {
    kind: 'template',
    title: key.name,
    highlightAnchorId: key.vaccinationId
      ? `vaccination-${key.vaccinationId}`
      : undefined,
    render: (ctx) => (
      <VaccinationsTemplate
        rows={rows}
        patient={ctx?.patient}
        filename={ctx?.filename}
        generatedAt={new Date()}
        highlightRowId={key.vaccinationId}
      />
    ),
  };
};
